import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import styles from './TagSelector.module.css';
import { Modal } from '../../../../components/UI/Modal/Modal';
import { Button } from '../../../../components/UI/Button/Button';
import { useApi } from '../../../../hooks/useApi';
import { ReviewTagModel } from '../../../../models/reviewTags';
import { CreateReviewTagRequest } from '../../../../api/reviews/dto/ReviewTagsRequest.dto';

interface TagSelectorProps {
  value?: string[];
  onChange: (tagIds: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const TagSelector: React.FC<TagSelectorProps> = observer(
  ({
    value = [],
    onChange,
    placeholder = 'Поиск тегов...',
    disabled = false,
  }) => {
    const api = useApi();
    const [tags, setTags] = useState<ReviewTagModel[]>([]);
    const [filteredTags, setFilteredTags] = useState<ReviewTagModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editingTag, setEditingTag] = useState<ReviewTagModel | null>(null);
    const [editLoading, setEditLoading] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedTags = tags.filter((tag) => value.includes(tag.id));

    // Вспомогательные функции для работы с переводами
    const getTranslation = (
      translations: ReviewTagModel['translations'],
      locale: 'ru' | 'en',
    ) => {
      return translations.find((t) => t.locale === locale);
    };

    const getTagDisplayName = (
      tag: ReviewTagModel,
      locale: 'ru' | 'en' = 'ru',
    ) => {
      const translation = getTranslation(tag.translations, locale);
      return translation?.name || `Тег ${tag.id}`;
    };

    // Загрузка тегов с поиском
    const loadTags = async (searchText: string = '') => {
      setLoading(true);
      try {
        const response = await api.reviews.getReviewTags({
          limit: 20,
          search: searchText.trim() || undefined,
        });
        if (response.success && response.body) {
          const allTags = response.body.tags || [];
          setTags(allTags);
          // Фильтруем только те теги, которые не выбраны
          const filtered = allTags.filter((tag) => !value.includes(tag.id));
          setFilteredTags(filtered);
        }
      } catch (error) {
        // Ошибка будет показана в UI
      } finally {
        setLoading(false);
      }
    };

    // Дебаунс для поиска
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
      null,
    );

    const debouncedSearch = (query: string) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const timeout = setTimeout(() => {
        loadTags(query);
      }, 300); // Задержка 300мс

      setSearchTimeout(timeout);
    };

    useEffect(() => {
      loadTags();
    }, []);

    useEffect(() => {
      // Очистка таймаута при размонтировании
      return () => {
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }
      };
    }, [searchTimeout]);

    // Обработчики событий
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);
      setIsDropdownOpen(true);
      // Запускаем поиск с дебаунсом
      debouncedSearch(query);
    };

    const handleSearchFocus = () => {
      setIsDropdownOpen(true);
      // Если поле пустое, загружаем первые 20 тегов
      if (!searchQuery.trim()) {
        loadTags();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleTagSelect = (tag: ReviewTagModel) => {
      if (!value.includes(tag.id)) {
        onChange([...value, tag.id]);
        setSearchQuery('');
        setIsDropdownOpen(false);
      }
    };

    const handleTagRemove = (tagId: string) => {
      onChange(value.filter((id) => id !== tagId));
    };

    // Создание нового тега
    const handleCreateTag = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation(); // Предотвращаем всплытие события
      setCreateLoading(true);

      try {
        const formData = new FormData(event.currentTarget);
        const tagData: CreateReviewTagRequest = {
          translations: [
            {
              locale: 'ru',
              name: formData.get('name_ru') as string,
            },
            {
              locale: 'en',
              name: formData.get('name_en') as string,
            },
          ],
        };

        const response = await api.reviews.createReviewTag(tagData);
        if (response.success && response.body) {
          setTags((prev) => [...prev, response.body as ReviewTagModel]);
          setIsCreateModalOpen(false);
          (event.target as HTMLFormElement).reset();
        }
      } catch (error) {
        // Ошибка будет показана в UI
      } finally {
        setCreateLoading(false);
      }
    };

    // Редактирование тега
    const handleEditTag = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation(); // Предотвращаем всплытие события
      if (!editingTag) {
        return;
      }

      setEditLoading(true);

      try {
        const formData = new FormData(event.currentTarget);
        const tagData = {
          translations: [
            {
              locale: 'ru' as const,
              name: formData.get('name_ru') as string,
            },
            {
              locale: 'en' as const,
              name: formData.get('name_en') as string,
            },
          ],
        };

        const response = await api.reviews.updateReviewTag(
          editingTag.id,
          tagData,
        );
        if (response.success && response.body) {
          setTags((prev) =>
            prev.map((tag) =>
              tag.id === editingTag.id
                ? (response.body as ReviewTagModel)
                : tag,
            ),
          );
          setEditingTag(null);
        }
      } catch (error) {
        // Ошибка будет показана в UI
      } finally {
        setEditLoading(false);
      }
    };

    // Удаление тега
    const handleDeleteTag = async (tagId: string) => {
      if (!confirm('Вы уверены, что хотите удалить этот тег?')) {
        return;
      }

      try {
        const response = await api.reviews.deleteReviewTag(tagId);
        if (response.success) {
          setTags((prev) => prev.filter((tag) => tag.id !== tagId));
          onChange(value.filter((id) => id !== tagId));
        }
      } catch (error) {
        // Ошибка будет показана в UI
      }
    };

    return (
      <div className={styles.tagSelector}>
        <div className={styles.selectorHeader}>
          <h3 className={styles.title}>Теги</h3>
        </div>

        <div className={styles.searchContainer}>
          <input
            ref={inputRef}
            type="text"
            className={styles.searchInput}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            placeholder={placeholder}
            disabled={disabled}
          />
          <button
            type="button"
            className={styles.addButton}
            onClick={() => setIsCreateModalOpen(true)}
            disabled={disabled}
          >
            +
          </button>

          {isDropdownOpen && (
            <div ref={dropdownRef} className={styles.dropdown}>
              {loading ? (
                <div className={styles.loading}>Загрузка...</div>
              ) : filteredTags.length > 0 ? (
                filteredTags.map((tag) => (
                  <div
                    key={tag.id}
                    className={styles.dropdownOption}
                    onClick={() => handleTagSelect(tag)}
                  >
                    <span className={styles.optionText}>
                      {getTagDisplayName(tag)}
                    </span>
                    <div className={styles.optionActions}>
                      <button
                        type="button"
                        className={styles.editButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTag(tag);
                        }}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className={styles.deleteButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTag(tag.id);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noResults}>Теги не найдены</div>
              )}
            </div>
          )}
        </div>

        <div className={styles.selectedTags}>
          {selectedTags.map((tag) => (
            <div key={tag.id} className={styles.tagChip}>
              <span>{getTagDisplayName(tag)}</span>
              <button
                type="button"
                className={styles.removeTagButton}
                onClick={() => handleTagRemove(tag.id)}
                disabled={disabled}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {/* Модальное окно создания тега */}
        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          title="Создать тег"
        >
          <div onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleCreateTag} className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Название (Русский)</label>
                <input
                  type="text"
                  name="name_ru"
                  className={styles.formInput}
                  required
                  maxLength={120}
                  placeholder="Введите название тега на русском"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Название (English)</label>
                <input
                  type="text"
                  name="name_en"
                  className={styles.formInput}
                  required
                  maxLength={120}
                  placeholder="Enter tag name in English"
                />
              </div>

              <div className={styles.modalActions}>
                <Button
                  type="button"
                  appearance="back"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCreateModalOpen(false);
                  }}
                  disabled={createLoading}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  appearance="auth"
                  disabled={createLoading}
                >
                  {createLoading ? 'Создание...' : 'Создать'}
                </Button>
              </div>
            </form>
          </div>
        </Modal>

        {/* Модальное окно редактирования тега */}
        <Modal
          isOpen={!!editingTag}
          onClose={() => setEditingTag(null)}
          title="Редактировать тег"
        >
          <div onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleEditTag} className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Название (Русский)</label>
                <input
                  type="text"
                  name="name_ru"
                  className={styles.formInput}
                  required
                  maxLength={120}
                  defaultValue={
                    editingTag
                      ? getTranslation(editingTag.translations, 'ru')?.name ||
                        ''
                      : ''
                  }
                  onChange={(e) =>
                    setEditingTag((prev) => {
                      if (!prev) {
                        return null;
                      }
                      return {
                        ...prev,
                        translations: prev.translations.map((t) =>
                          t.locale === 'ru'
                            ? { ...t, name: e.target.value }
                            : t,
                        ),
                      };
                    })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Название (English)</label>
                <input
                  type="text"
                  name="name_en"
                  className={styles.formInput}
                  required
                  maxLength={120}
                  defaultValue={
                    editingTag
                      ? getTranslation(editingTag.translations, 'en')?.name ||
                        ''
                      : ''
                  }
                  onChange={(e) =>
                    setEditingTag((prev) => {
                      if (!prev) {
                        return null;
                      }
                      return {
                        ...prev,
                        translations: prev.translations.map((t) =>
                          t.locale === 'en'
                            ? { ...t, name: e.target.value }
                            : t,
                        ),
                      };
                    })
                  }
                />
              </div>

              <div className={styles.modalActions}>
                <Button
                  type="button"
                  appearance="back"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingTag(null);
                  }}
                  disabled={editLoading}
                >
                  Отмена
                </Button>
                <Button type="submit" appearance="auth" disabled={editLoading}>
                  {editLoading ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    );
  },
);

export default TagSelector;
